// Content for Śrīmad-Bhāgavatam 6.2.9-10 commentary topic

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySloka: {
        reference: 'Srimad Bhagavatam 6.2.9-10',
        sloka: `stenaḥ surā-po mitra-dhrug
brahma-hā guru-talpa-gaḥ
strī-rāja-pitṛ-go-hantā
ye ca pātakino 'pare
sarveṣām apy aghavatām
idam eva suniṣkṛtam
nāma-vyāharaṇaṁ viṣṇor
yatas tad-viṣayā matiḥ`,
        translation:
            'The chanting of the holy name of Lord Viṣṇu (<strong>nāma-vyāharaṇam viṣṇoḥ</strong>) is the best process of atonement (<strong>idam eva suniṣkṛtam</strong>) for a thief (<strong>stenaḥ</strong>), for a drunkard (<strong>surā-paḥ</strong>), for one who betrays a friend or relative (<strong>mitra-dhruk</strong>), for one who kills a brāhmaṇa (<strong>brahma-hā</strong>), for one who indulges in sex with the wife of his guru or another superior (<strong>guru-talpa-gaḥ</strong>), for one who kills women, the king, his father, or cows (<strong>strī-rāja-pitṛ-go-hantā</strong>), and for all other sinful men (<strong>ye ca pātakinaḥ apare sarveṣām api aghavatām</strong>). Simply by chanting the holy name of Lord Viṣṇu (<strong>yataḥ</strong>), such sinful persons attract the attention of the Supreme Lord (<strong>tad-viṣayā matiḥ</strong>).',
    },
    blocks: [
        {
            type: 'heading',
            headingText: 'Nāma Is Suniṣkṛtam — It Destroys the Root of Sin',
            content:
                'The Yamadūtas raise the central challenge of the chapter: even if the name can destroy sins, how can a mere unconscious utterance — <em>nāmābhāsa</em> — atone for thousands of the gravest sins?',
        },
        {
            type: 'dialogue',
            objection:
                '"The name of the Lord may destroy sins, but how can <em>nāmābhāsa</em> be the atonement for thousands of grave sins which cannot be destroyed by thousands of twelve year vows?"',
            response:
                '<em>Stenaḥ</em> means a gold thief. <strong>Chanting is the best atonement (<em>suniṣkṛtam</em>), since it destroys the root of sin.</strong> A twelve year vow of atonement, though it destroys the effects of sin, cannot destroy the root of sin. That is not the only result, because from chanting the name of the Lord, the Lord\'s mind dwells on that person who chants (<em>tad-viṣayā-matiḥ</em>). <strong>The Lord thinks, "This person belongs to me. I will always protect him."</strong> This is Śrīdhara Svāmī\'s meaning of the phrase.',
        },
        {
            type: 'text',
            content:
                'The servants of Viṣṇu said "Hearing his name being chanted, and remembering Ajāmila, the Lord ordered us to bring Ajāmila, who was chanting his name." Then how much more the Lord will remember a person who chants the Lord\'s name with a service attitude! This is the implication. To show this to the servants of Yama, the servants of Viṣṇu said that Ajāmila\'s chanting at the time of death had destroyed all his sins, but actually,<br /><br /><strong>from the first time that Ajāmila called his son Nārāyaṇa — out of all the times he called, all his sins had been destroyed. The rest of the chanting after that produced <em>bhakti</em>.</strong> <strong>The past tense of <em>yad vyājahāra</em> (he chanted) in verse 7 also indicates that from the first time he chanted the name, all sins had been destroyed.</strong><br /><br />The word <em>vivaśaḥ</em> in that verse means "spontaneously, out of affection for his child."',
        },
        {
            type: 'heading',
            headingText: 'Scriptural Support — Nāma Burns Past, Present and Future Sin',
            content:
                'Three śāstric testimonies confirm what no vow or austerity can claim:',
        },
        {
            type: 'dialogue',
            objection:
                '"But after chanting repeatedly, he committed repeated sins such as going to the prostitute and drinking wine. In order to destroy those sins he had to chant at the end of his life, since there was again appearance of sin."',
            response:
                'This cannot be said, because it will be said later <em>vaikuṇṭha-nāma-grahaṇam aśeṣāgha-haram</em>: chanting the Lord\'s name destroys unlimited sins. (SB 6.2.16) The following verses also show that chanting releases one from the bondage of <em>saṁsāra</em>.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Hari-bhakti-vilāsa 11.339',
                sloka: `vartamānam ca yat pāpaṁ
yad bhūtaṁ yad bhaviṣyati
tat sarvaṁ nirdahatyāśu
govindānala-kīrtanāt`,
                translation:
                    'All sins in the present, past and future are quickly destroyed by chanting the name of Govinda, which is like fire.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 6.16.44',
                sloka: `yan-nāma sakṛc chravaṇāt
pukkaśo 'pi vimucyate saṁsārāt`,
                translation:
                    'By hearing one of your names once, even the outcaste is delivered from the material world.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 5.1.35',
                sloka: `naivaṁ-vidhaḥ puruṣa-kāra urukramasya
puṁsāṁ tad-aṅghri-rajasā jita-ṣaḍ-guṇānām
citraṁ vidūra-vigataḥ sakṛd ādadīta
yan-nāmadheyam adhunā sa jahāti bandham`,
                translation:
                    'Such power is not surprising from persons who have conquered the six senses by the dust from the lotus feet of the Lord, since even an outcaste becomes immediately free of bondage of <em>karma</em> by chanting the Lord\'s name even once.',
            },
        },
        {
            type: 'text',
            content:
                '<strong>Because there is no mention of a particular time span for the effect of <em>nāmābhāsa</em> to occur, it should be understood that by the first chanting there is destruction of all sins, all desires and as well, ignorance, which is the root cause, because only then sin will not reappear in the future.</strong>',
        },
        {
            type: 'dialogue',
            objection:
                '"Why then did sin not leave Ajāmila after chanting the first time? How could he continue to be attached to the woman and commit sin for so long, if all his sins had been destroyed?"',
            response:
                '<strong>Like the <em>karmas</em> of the <em>jīvanmukta</em> which remain for some time simply as impressions, Ajāmila\'s sins which lasted till his death were like the bites of a toothless snake and did not generate results.</strong><br /><br />Moreover the Lord himself will cause continuance of sin in order that other philosophies (<em>karma-kāṇḍa</em> etc.) will not be completely uprooted.',
        },
        {
            type: 'heading',
            headingText: 'The Gravest Offense — Interpreting Nāma\'s Glories as Exaggeration',
            content:
                'If one were to explain the scriptural statements concerning the powers of the name to be exaggerated praise of the Lord\'s name, then one would be committing offense. <em>Tathārtha-vādo hari-nāmni kalpanam</em>: it is an offense to interpret the name or think its powers are exaggeration. (<em>Padma Purāṇa</em>)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Padma Purāṇa',
                sloka: `nāmāśrayaḥ kadācit syāt
taraty eva sa nāmataḥ
nāmno hi sarva suhṛdo
hy aparādhāt pataty adhaḥ`,
                translation:
                    'One who takes shelter of the name at any time crosses material life by the name.<br />One falls to hell from offense to the name of the Lord, who is the friend of all beings.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Kātyāyana Saṁhitā, Padma Purāṇa',
                sloka: `arthavadam harer-namni
sambhavayati yo narah
sa papistho manusyanam
niraye patati sphutam`,
                translation: 'Those most sinful of men who interpret the name of the Lord fall to hell.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Padma Purāṇa',
                sloka: `yan-nāma-kīrtana-phalaṁ vividhaṁ niśamya
na śraddadhāti manute yad utārthavādam
yo mānuṣas tam iha dukḥa-caye kṣipāmi
saṁsāra-ghora-vividhārti nipīḍitāṇgam`,
                translation:
                    'That person who, even after hearing about the wonderful results of chanting the Lord\'s name, refuses to develop sincere faith in the name, and on the contrary, interprets the name, is hurled by me into the deep gloom of material nescience after being dragged through excruciating suffering.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Jaimini Saṁhitā',
                sloka: `śruti-smṛti-purāṇeṣu
nāma-māhātmya-vādiṣu
ye 'rthavāda iti bruyur
na teṣāṁ niraya-kṣayaḥ`,
                translation:
                    'Persons who interpret the <em>śrutis, smṛtis,</em> and purāṇas, which wonderfully glorify the Lord\'s name, stay in hell forever.',
            },
        },
        {
            type: 'text',
            content:
                '<strong>Thus from scriptures like <em>Padma Purāṇa</em> and Kātyāyana-saṁhitā there are thousands of statements showing fall down from interpreting the name.</strong>',
        },
        {
            type: 'text',
            content: 'Parīkṣit has just said:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 6.1.10',
                sloka: `kvacin nivartate 'bhadrāt
kvacic carati tat punaḥ
prāyaścittam atho 'pārthaṁ
manye kuñjara-śaucavat`,
                translation:
                    'After withdrawing from sin one commits the sin again. I think atonement is useless, like an elephant bathing.',
            },
        },
        {
            type: 'text',
            content:
                '<strong>He has condemned atonements because of seeing that the tendency for sin remains, but he does not criticize <em>bhakti</em>, though seeing sinful tendencies in some of the devotees.</strong> <strong>And Ajāmila, a sinner, attained Vaikuṇṭha by the strength of <em>nāmābhāsa</em>, but <em>smārtas</em> and others, though knowing scriptures, and though chanting the name, continue their existence in frightful <em>saṁsāra</em> because of the offense of interpreting the name.</strong>',
        },
        {
            type: 'text',
            content:
                'But one should not worry that everyone will immediately become liberated on seeing such power in the name. <strong>Though sin is completely uprooted just by chanting the name even once, in most cases the name shows its fruits to the world after some time, just as fruit trees bear fruit after some time only, not immediately.</strong><br /><br />And in some cases the name does not show its effects at all, in order that the material scriptures (such as <em>karma-kāṇḍa</em>) are not completely destroyed (by loss of faith in them). Then, after doing this, the name takes the person who has chanted without offense to the Lord\'s abode. This conclusion should be understood.',
        },
        {
            type: 'heading',
            headingText: 'Bhakti-Devī and the Merchant — Primary and Secondary Shelter',
            content:
                'A deeper challenge now arrives: if the name is so powerful, why do even <em>karmīs, jñānīs</em> and <em>bhaktas</em> still experience the consequences of sin?',
        },
        {
            type: 'dialogue',
            objection:
                '"I accept that because of offense to the name, those who interpret the name as an exaggeration go to hell. However, if chanting the name destroys all sins, all <em>karmīs, jñānīs, yogīs</em> and <em>bhaktas</em> should not go to hell for illicit sex or violence if they chant. And if the name does not destroy all sin, all these people, and even the <em>bhakta,</em> should go to hell to suffer the results of their sin, even if they chant."',
            response:
                'Though a merchant protects a person under his shelter according to the degree that the person surrenders to him, if the person offends him, the merchant becomes displeased with him and does not give protection to that surrendered person. But one should not think that the merchant is incapable of protecting him. And according to the degree that the offense diminishes, the merchant begins to show mercy to that person. When the offense is completely gone, the merchant shows all mercy. The name is similar.<br /><br /><strong>Those who take shelter of Bhakti-devī, representing the name, as a secondary practice, in order to bring out results of <em>karma</em> and <em>jñāna</em>, are called <em>karmīs</em> or <em>jñānīs,</em> even though <em>bhakti</em> is present in a minor position.</strong> This is according to the rule that things are named according to the predominant factor. They are thus not called Vaiṣṇavas. By their natures they are offenders to the name in one aspect, for it is said <em>dharma-vratatyāgahutādi-sarvaśubhakriyā-sāmyamapi pramādaḥ</em>: the eighth offense is to consider the name equivalent to <em>dharma, vratas,</em> sacrifices and other <em>karma-kāṇḍa</em> rites.<br /><br /><strong>If considering the name to be equal to <em>karma</em> and <em>dharma</em> is an offense, then the offense is much more if one considers the name secondary to <em>karma</em> and <em>dharma</em>, being a mere limb of <em>karma</em> or <em>dharma</em>.</strong>',
        },
        {
            type: 'text',
            content:
                'Though recognizing that they have offended her, out of compassion, Bhakti-devī thinks, "<em>Karma-yoga</em> and these other processes should not be fruitless" since they have accepted a small portion of her shelter. Thus, though she has become only a limb of <em>karma,</em> she gives the results of <em>karma, jñāna</em> and other processes without obstruction.<br /><br /><strong>Similarly, when <em>bhakti</em> is a limb of atonement, she destroys the sins in those persons practicing atonements. It is not otherwise. And those who do not perform atonements go to hell to experience the results of their sins. However, Vaiṣṇavas do not need to perform atonements.</strong><br /><br />Furthermore, if those persons commit other offenses, such as interpreting the name or committing offense to the devotee, and then perform <em>dharma</em> and other process, Bhakti-devī gives them no results for their efforts, even though she is still a limb of <em>dharma</em> and other process.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Padma Purāṇa, Brāhma-khaṇḍa',
                sloka: `ke te 'paradhā vipendra
namno bhagavatah krtāḥ
vinighnanti nṛnam krtyam
prakrtam hy anayanti ca`,
                translation:
                    'O brāhmaṇa! Offenses to the name destroy men\'s pious actions and lead them to material world.',
            },
        },
        {
            type: 'text',
            content:
                'If those persons become free from offense and dedicate themselves to chanting or other <em>bhakti</em> processes, they will get results for their <em>karma</em> and <em>jñāna</em> in proportion to the destruction of offenses. However, with complete destruction of offense by association with devotees, attainment of the results of chanting is certain, by the direct mercy of Bhakti-devī.',
        },
        {
            type: 'dialogue',
            objection:
                '"From the words of the servants of Yama it is understood that Ajāmila was previously involved in <em>karma.</em> (He was not a devotee, but performed <em>bhakti</em> secondarily, and therefore all his sins should not have been removed by chanting.)"',
            response:
                'That is true, and by sinful acts like drinking, his status as a <em>brāhmaṇa</em> was destroyed, what to speak of his pious acts of <em>karma.</em> It will be explained "Ajāmila was a <em>brāhmaṇa</em> who because of bad association had given up all <em>brahminical</em> culture and religious principles. Becoming most fallen, he stole, drank and performed other abominable acts. He even kept a prostitute." (SB 6.2.45)<br /><br /><strong>When his pious <em>karmas</em> were destroyed, his secondary <em>bhakti</em> was also destroyed. Then pure <em>bhakti</em> appeared when he called out the name of his son Nārāyaṇa.</strong>',
        },
        {
            type: 'dialogue',
            objection:
                '"But if there is a scriptural rule that one should perform <em>bhakti</em> as a limb of <em>karma</em> or <em>jñāna</em>, how can that be offensive?"',
            response:
                '<strong>One should not recognize as authoritative scripture those statements which permit secondary <em>bhakti</em> out of mercy for persons of crooked mentality, who have no faith in <em>bhakti</em> but faith in <em>karma</em> and <em>jñāna,</em> and do not believe statements like "All processes accomplish their results by <em>bhakti</em> alone; by a particle of <em>bhakti</em> the greatest sins are destroyed." In attaining Svarga through animal sacrifices by following rules, the fault of violence is not destroyed. Similarly though the offender attains the results of <em>karma</em> and <em>jñāna</em> by secondary <em>bhakti</em> according to rules, his offense is not destroyed.</strong>',
        },
        {
            type: 'text',
            content:
                '<strong>Those offenders who accept a <em>Vaiṣṇava guru</em> by Vaiṣṇava initiation take shelter of Bhakti-devī purely or as the primary process (but mixed), and then worship the Lord by chanting, are called Vaiṣṇavas.<br /><br />According to the degree of <em>bhakti,</em> they attain destruction of offenses, and according to the degree of Bhakti-devī\'s mercy, they attain the principal result of <em>bhakti (prema).</em></strong> The Lord himself says:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.14.26',
                sloka: `yathā yathātmā parimṛjyate 'sau
mat-puṇya-gāthā-śravaṇābhidhānaiḥ
tathā tathā paśyati vastu sūkṣmaṁ
cakṣur yathaivāñjana-samprayuktam`,
                translation:
                    'To the degree that the <em>ātmā</em> becomes purified by hearing and chanting my glories, a person is able to perceive my real form and qualities, and experience their sweetness, just as the eye when smeared with special ointment is able to see finer objects.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.2.42',
                sloka: `bhaktiḥ pareśānubhavo viraktir
anyatra caiṣa trika eka-kālaḥ
prapadyamānasya yathāśnataḥ syus
tuṣṭiḥ puṣṭiḥ kṣud-apāyo 'nu-ghāsam`,
                translation:
                    'Devotion, direct experience of the Supreme Lord, and detachment from other things — these three occur simultaneously for one who has taken shelter of Kṛṣṇa, in the same way that pleasure, fullness of the stomach and relief from hunger are experienced simultaneously, with each bite, for a person engaged in eating.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.2.17',
                sloka: `śṛṇvatāṁ sva-kathāḥ kṛṣṇaḥ
puṇya-śravaṇa-kīrtanaḥ
hṛdy antaḥstho hy abhadrāṇi
vidhunoti suhṛt-satām`,
                translation:
                    'Kṛṣṇa, who purifies a person by the processes of hearing and chanting and who is the benefactor of the devotees who hear about him, enters the hearts of the devotees and destroys their sins.',
            },
        },
        {
            type: 'text',
            content:
                'By these statements it is understood that those persons who committed offense gradually ascend through the fourteen stages of <em>bhakti</em>. In those cases, faith and other steps are prescribed. And in this chapter also it is said <em>guṇānuvādaḥ khalu sattva-bhāvanaḥ</em>: chanting the Lord\'s glories is the process for purification. (SB 6.2.12) When all their offenses are gradually destroyed and they attain the Lord, they are liberated from this world.',
        },
        {
            type: 'heading',
            headingText: 'The Sprout of Bhakti Is Imperishable — Three Types of Vaiṣṇavas',
            content:
                'However, for those persons without offense, attainment of the Lord is quick. Such persons have two stages: chanting the Lord\'s name, and attaining Vaikuṇṭha. Such is the case of Ajāmila.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Mahābhārata 13.135.131',
                sloka: `na vāsudeva-bhaktānām
aśubhaṁ vidyate kvacit
janma-mṛtyu-jarā-vyādhi-
bhayaṁ vāpy upajāyate`,
                translation:
                    'The devotees of Vāsudeva have no misfortune at all. They surpass birth, death, old age and disease.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 4.25.29',
                sloka: `sva-dharma-niṣṭhaḥ śata-janmabhiḥ pumān
viriñcatām eti tataḥ paraṁ hi mām
avyākṛtaṁ bhāgavato 'tha vaiṣṇavaṁ
padaṁ yathāhaṁ vibudhāḥ kalātyaye`,
                translation:
                    'A person fixed in <em>dharma</em> attains the post of Brahmā after a hundred births and by more pious acts a person attains me. But the devotee attains the abode of Vaikuṇṭha after leaving his body, just as I reside there in another form, and the <em>devatās</em> who are qualified go there after destroying their subtle bodies.',
            },
        },
        {
            type: 'text',
            content:
                'However, some devotees without offense, who desire to attain a special <em>prema,</em> are delayed in attaining the Lord. For instance Jaḍa Bharata took three lives to attain the Lord.',
        },
        {
            type: 'text',
            content:
                'Among those devotees having offense, if some, because of not worshipping the Lord properly, do not destroy previous sins, and continue to sin and to be offenders, they do not go to hell after leaving the body. Yama says:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Yamarāja',
                sloka: `sva-puruṣam abhivīkṣya pāśa-hastaṁ
vadati yamaḥ kila tasya karṇa-mūle
parihara madhusūdana prapannān
prabhur aham anya-nṛṇāṁ na vaiṣṇavānām`,
                translation:
                    'Yamarāja seeing his follower with noose in his hands says in his ear: Do not take devotees surrendered to Madhusūdana. I am the master of other men, but not the Vaiṣṇavas.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 6.3.27',
                sloka: `te deva-siddha-parigīta-pavitra-gāthā
ye sādhavaḥ samadṛśo bhagavat-prapannāḥ
tān nopasīdata harer gadayābhiguptān
naiṣāṁ vayaṁ na ca vayaḥ prabhavāma daṇḍe`,
                translation:
                    'Do not approach those who have surrendered to the Lord, who see everything equally, who have proper conduct and who are praised with pure narrations by the <em>devatās</em> and Siddhas, since they are protected by the club of the Lord. We, including Brahmā and even time, do not have power to punish them.',
            },
        },
        {
            type: 'text',
            content: 'The servants of Yama say:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Padma Purāṇa',
                sloka: `prāhāsmān yamunā-bhrātā
sādaraṁ hi punaḥ punaḥ
bhavadbhir vaiṣṇavās tyājyā`,
                translation: 'Yama repeatedly has told us with care, "You must leave the Vaiṣṇavas."',
            },
        },
        {
            type: 'text',
            content: 'Moreover the Lord himself says:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.29.20',
                sloka: `na hy aṅgopakrame dhvaṁso
mad-dharmasyoddhavāṇv api
mayā vyavasitaḥ samyaṅ
nirguṇatvād anāśiṣaḥ`,
                translation:
                    'O Uddhava! Because I have personally established it, this process of <em>niṣkāma-bhakti</em> is beyond the <em>guṇas.</em> Even by starting but not completing the process, there is no destruction of results.',
            },
        },
        {
            type: 'text',
            content:
                '<strong>Thus, since even the sprout of <em>bhakti</em> is imperishable, unaffected by sin and always productive, a person will definitely take birth only in order that <em>bhakti</em> bears leaves and fruit in the future, and not because of temporary sin and piety.</strong> <em>Na karma-bandhanaṁ janma vaiṣṇavānāṁ ca vidyate</em>: the worshippers of Viṣṇu do not have birth due to <em>karma.</em> Thus after the destruction of sin and destruction of offenses by chanting the name, which is caused by impressions of previous <em>bhakti,</em> persons attain the Lord by the mercy of Bhakti-devī.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.5.19',
                sloka: `na vai jano jātu kathañcanāvrajen
mukunda-sevy anyavad aṅga saṁsṛtim
smaran mukundāṅghry-upagūhanaṁ punar
vihātum icchen na rasa-graho janaḥ`,
                translation:
                    'Oh! The person who serves Mukunda will never under any condition return to the material world, unlike practitioners of other processes. Remembering the embrace of the Lord\'s lotus feet, eager for that taste he has experienced, he will not desire to give up those feet again.',
            },
        },
        {
            type: 'text',
            content:
                'In the above verse <em>anyavat</em> means "like those practicing <em>karma</em>." The devotee will not attain the world of piety and sin (caused by <em>karma</em>), but will accept the world of happiness and distress given by the Lord.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 10.87.40',
                sloka: `tvad avagamī na vetti bhavad-uttha-śubhāśubhayor
guṇa-viguṇānvayāṁs tarhi deha-bhṛtāṁ ca giraḥ
anu-yugam anv-ahaṁ sa-guṇa gīta-paramparayā
śravaṇa-bhṛto yatas tvam apavarga-gatir manu-jaiḥ`,
                translation:
                    'When a person realizes you, he no longer cares about his good and bad fortune arising from past pious and sinful acts, since it is you alone who control this good and bad fortune. Such a realized devotee also disregards what ordinary living beings say about him. Every day he fills his ears with your glories, which are recited in each age by the unbroken succession of Manu\'s descendants, and thus you become his ultimate salvation.',
            },
        },
        {
            type: 'text',
            content:
                'Those who have not destroyed offenses to the name will continue to experience the undestroyed effects of sin. When, by increase of <em>bhakti</em> by practicing it, the offenses to the name are destroyed, the root of sin will be destroyed and the person will immediately attain the Lord. But then, in order to increase the <em>bhakti,</em> those devotees may even take one, two or three life times to attain the Lord. The "material happiness" seen in those devotees arises from the practice of <em>bhakti</em> (it is not <em>karma</em>). It is said:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.2.9',
                sloka: `dharmasya hy āpavargyasya
nārtho'rthāyopakalpate
nārthasya dharmaikāntasya
kāmo lābhāya hi smṛtaḥ`,
                translation:
                    'The material results are not suitable as the goal for the person dedicated to higher spiritual goals. The desire of the person dedicated to the higher path is not for attainment of material assets.',
            },
        },
        {
            type: 'text',
            content:
                'The "suffering" seen in those devotees is given by the Lord, who is skilful at increasing the devotion of his devotee, and who is like a doctor who makes a person fast and gives bitter medicine for increasing the patient\'s appetite.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 10.88.8',
                sloka: `yasyāham anugṛhṇāmi
hariṣye tad-dhanaṁ śanaiḥ
tato 'dhanaṁ tyajanty asya
svajanā duḥkha-duḥkhitam`,
                translation:
                    'If I especially favor someone, I gradually deprive him of his wealth. Then the relatives and friends of such a poverty-stricken man abandon him. In this way he suffers one distress after another.',
            },
        },
        {
            type: 'text',
            content:
                '<strong>Among the ten offenses, the very strong effects of interpreting the name, thinking its glories are exaggeration and equating it to pious acts — are obstacles to being a Vaiṣṇava. Among the other offenses two are very strong — offending the devotees and committing sin on the strength of chanting.</strong> Those two are particularly described in a frightening way.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Padma Purāṇa',
                sloka: `yataḥ khyātiṁ yātaṁ katham
u sahate tad vigarhām`,
                translation: 'How can the name tolerate criticism of those who spread the name?',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Padma Purāṇa',
                sloka: `nāmno balādyasya hi pāpabuddhir-
na vidyate tasya yamair hi śuddhiḥ`,
                translation:
                    'For a person who commits sin on the strength of chanting there is no purification by the servants of Yama.',
            },
        },
        {
            type: 'text',
            content:
                '<strong>There is no other means of destroying the effects of these two offenses than to continually chant the name while suffering appropriately for the offense.</strong> The effects of the other offenses will be destroyed simply by continual chanting (without the suffering).',
        },
        {
            type: 'dialogue',
            objection:
                'Some people make the following proposition. "Those offenders of the name who are without <em>karma</em> and <em>jñāna</em>, and who practice <em>bhakti</em> with hearing and chanting but have not taken initiation because they have not surrendered to the feet of a <em>guru</em> are still called Vaiṣṇavas. The word vaiṣṇava is defined as \'the person who takes Viṣṇu as his object of worship\' according to Pāṇini\'s sūtras 4.2.24 and 4.3.95. Thus Vaiṣṇavas are those who have made Viṣṇu the object of worship by accepting initiation but also those who make Viṣṇu the object of worship simply by worship, since there is no other word to describe the two types of people. So the latter type of persons also should not fall to hell."',
            response:
                'This however is not correct because one cannot attain the Lord easily without <em>guru.</em>',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.20.17',
                sloka: `nṛ-deham ādyaṁ su-labhaṁ su-durlabhaṁ
plavaṁ su-kalpaṁ guru-karṇadhāram
mayānukūlena nabhasvateritaṁ
pumān bhavābdhiṁ na taret sa ātma-hā`,
                translation:
                    'Having attained the human body, rarely attained, but attained easily sometimes by good fortune, which is like a well-constructed boat, with the <em>guru</em> as the captain, pushed by the favorable wind of serving me, a person who does not cross the ocean of material existence is a killer of himself.',
            },
        },
        {
            type: 'text',
            content:
                'Therefore it should be explained that there is no other way of attaining the Lord than by becoming a devotee who has surrendered to the feet of a <em>guru</em>, who will be attained in another birth by the power of worship.',
        },
        {
            type: 'dialogue',
            objection:
                '"But it is seen that Ajāmila, without surrendering to <em>guru</em> easily attained the Lord."',
            response:
                'This can be explained as follows. Those who, like cows or asses, make their senses pursue sense objects cannot know the Lord, <em>bhakti</em> or <em>guru</em> even in their dreams.<br /><br />But as in the case of Ajāmila, they can be delivered even without <em>guru</em> by chanting the name in <em>nāmābhāsa</em> without offense.<br /><br />Though it has been definitely ascertained that the Lord should be worshipped, that worship is the means of attaining him, that the <em>guru</em> is the person who teaches about worship, and that those who are devotees attain the Lord, it is also said:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Padyāvalī',
                sloka: `no dīkṣāṁ na ca sat-kriyāṁ na ca puraścaryāṁ manāg īkṣate
mantro 'yaṁ rasanā-spṛg eva phalati śrī-kṛṣṇa-nāmātmakaḥ`,
                translation:
                    'Chanting the holy name does not depend on initiation, pious activities or the <em>puraścaryā</em> regulative principles generally observed before initiation. The holy name does not wait for any of these activities. It is self-sufficient.',
            },
        },
        {
            type: 'text',
            content:
                'As well, we have the example of Ajāmila. <strong>But one who thinks, "What is the benefit of taking the trouble to accept <em>guru</em>? I will attain the Lord just by chanting the name," will not attain the Lord, because of the offense of offending <em>guru</em> (third offense).</strong><br /><br />However, in this life or another life, if he surrenders to the feet of <em>guru</em> after destroyed his offense, he will then attain the Lord.<br /><br />Some people say that persons who worship <em>devatās</em> should be treated like the <em>karmīs</em> with sin and offense. Others say that Bhakti-devī places them in an even lower category because they do not have general surrender. Thus it is said:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-Gītā 9.23',
                sloka: `ye 'py anya-devatā-bhaktā
yajante śraddhayānvitāḥ
te 'pi mām eva kaunteya
yajanty avidhi-pūrvakam`,
                translation:
                    'Those who are devoted to other gods and with faith worship them — they also worship me, but by the wrong method, O son of Kuntī.',
            },
        },
        {
            type: 'text',
            content: 'But for those who are simply offenders there is no deliverance at all. It is said:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-Gītā 16.19',
                sloka: `tān ahaṁ dviṣataḥ krūrān
saṁsāreṣu narādhamān
kṣipāmy ajasram aśubhān
āsurīṣv eva yoniṣu`,
                translation:
                    'I cast those hateful, cruel, and lowest of humans, constantly doing evil, into repeated birth and death in the wombs of demons.',
            },
        },
        {
            type: 'text',
            content:
                'But some persons also say that these offenders\' offenses are destroyed by mental absorption (in hatred) on the Lord since it is said:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 7.1.30',
                sloka: `kāmād dveṣād bhayāt snehād
yathā bhaktyeśvare manaḥ
āveśya tad-aghaṁ hitvā
bahavas tad-gatiṁ gatāḥ`,
                translation:
                    'Just as by <em>vaidhi-bhakti</em> one can attain one\'s spiritual goals, many persons have attained suitable forms, after absorbing their minds in the Lord out of lust, hatred, fear and family relationships filled with affection, and after giving up absorption in enmity of the Lord (in the case of hatred and fear).',
            },
        },
        {
            type: 'text',
            content: 'This idea is represented in verses such as the following:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Padma Purāṇa',
                sloka: `nāmāparādha-yuktānāṁ
nāmāny eva haranty agham
aviśrānti-prayuktāni
tāny evārtha-karāṇi ca`,
                translation:
                    'Even if in the beginning one chants the Hare Kṛṣṇa mantra with offenses, one will become free from such offenses by chanting again and again.',
            },
        },
        {
            type: 'text',
            content:
                'Others say that "absorption" means repeated meditation in order to be effective. Others say that there is no absolute rule when Kṛṣṇa appears in the world.<br /><br /><strong>Thus some persons without being absorbed in the Lord, but simply by being killed by the Lord — such as Narakāsura, Bāṇāsura and the troops of the Kauravas and others simply by the influence of seeing the Lord attained the Lord.</strong> This was previously mentioned in the Bhāgavatam.',
        },
        {
            type: 'divider',
            dividerLabel: 'End of Commentary',
        },
        {
            type: 'heading',
            headingText: 'The Full Spectrum — Six Outcomes, Four Offense Tiers',
            content:
                'Every person discussed above falls into one of six relationships to the holy name, and every offense falls into one of four tiers of severity. The table and flowchart below draw the whole commentary together in one place — as a category-by-category summary, and as the argument\'s own step-by-step flow. Switch between them with the toggle.',
        },
        {
            type: 'toggle',
            toggle: {
                defaultViewId: 'table',
                views: [
                    {
                        id: 'table',
                        label: 'Table',
                        icon: 'table',
                        blocks: [
                            {
                                type: 'table',
                                table: {
                                    headers: ['Category', 'Relationship to nāma', 'Outcome'],
                                    rows: [
                                        [
                                            'Pure Offenders',
                                            'No devotion; enmity to the Lord or His devotees',
                                            '<em>"There is no deliverance at all"</em> (BG 16.19) — cast into demonic wombs. Exception: those "absorbed" in the Lord even through hatred or fear (SB 7.1.30), or slain by Him directly — Narakāsura, Bāṇāsura, the Kaurava armies attained Him "by the influence of seeing" Him.',
                                        ],
                                        [
                                            'Devatā Worshippers',
                                            'Worship "by the wrong method" (BG 9.23); no general surrender to Bhakti-devī',
                                            'Disputed rank — some say equal to karmīs; others say Bhakti-devī places them lower, "because they do not have general surrender."',
                                        ],
                                        [
                                            'Karmīs / Jñānīs',
                                            'Take shelter of Bhakti-devī only "as a secondary practice" — bhakti is a mere limb of karma/jñāna',
                                            'Treating the name as secondary is worse than the literal 8th offense itself. Bhakti-devī still gives <em>"the results of karma, jñāna and other processes without obstruction,"</em> out of compassion — but not nāma\'s own fruit; they remain bound, since <strong>"Vaiṣṇavas do not need to perform atonements"</strong> (implying they still do).',
                                        ],
                                        [
                                            'Vaiṣṇavas with Offense — Gradually Purifying',
                                            'Initiated, bhakti held as primary, but offenses remain',
                                            '<em>"Ascend through the fourteen stages of bhakti,"</em> taking <em>"one, two or three life times to attain the Lord."</em> Happiness <em>"arises from the practice of bhakti (it is not karma)"</em>; suffering is the Lord acting <em>"like a doctor who makes a person fast and gives bitter medicine"</em> (SB 10.88.8).',
                                        ],
                                        [
                                            'Vaiṣṇavas with Offense — Continuing to Sin',
                                            'Initiated, but "not worshipping the Lord properly," continuing to sin',
                                            'Still untouchable — Yama himself says, <strong>"I am the master of other men, but not the Vaiṣṇavas."</strong> Future births occur only <em>"in order that bhakti bears leaves and fruit"</em> — <em>"na karma-bandhanaṁ janma,"</em> their birth is not due to karma.',
                                        ],
                                        [
                                            'Vaiṣṇavas without Offense',
                                            'Chanting held as pure, primary practice, free of nāma-aparādha',
                                            '<strong>"Two stages only: chanting the Lord\'s name, and attaining Vaikuṇṭha"</strong> — "such is the case of Ajāmila." Exception: those desiring "a special prema" may be delayed — Jaḍa Bharata "took three lives to attain the Lord."',
                                        ],
                                        { sectionLabel: 'By Type of Offense' },
                                        [
                                            'Disqualifying Offenses',
                                            'Interpreting nāma\'s glories as exaggeration; treating the name as equal to karma-kāṇḍa (8th offense); treating it as secondary to karma/dharma',
                                            'Named among "the very strong effects... obstacles to being a Vaiṣṇava." One who "refuses to develop sincere faith in the name, and on the contrary, interprets the name, is hurled... into the deep gloom of material nescience after being dragged through excruciating suffering" (Padma Purāṇa); interpreting the glory as exaggeration alone also causes one to "stay in hell forever" (Jaimini Saṁhitā). No remedy is named for this tier — scripture never states a way back.',
                                        ],
                                        [
                                            'The Two "Very Strong" Offenses',
                                            'Offending devotees; sinning on the strength of having chanted',
                                            '"How can the name tolerate criticism of those who spread the name?" / "no purification by the servants of Yama" for sinning on strength of chanting. <strong>"There is no other means of destroying the effects of these two offenses than to continually chant the name while suffering appropriately for the offense."</strong>',
                                        ],
                                        [
                                            'Disregarding Guru (the Third Offense)',
                                            'Thinking chanting alone suffices, guru unnecessary',
                                            'Will not attain the Lord because of "the offense of offending guru" — "a person who does not cross the ocean of material existence is a killer of himself" (SB 11.20.17). Remedy: surrendering to guru\'s feet, "in this life or another life."',
                                        ],
                                        [
                                            'All Other Offenses',
                                            'Any offense not named above',
                                            '<em>"The effects of the other offenses will be destroyed simply by continual chanting (without the suffering)."</em>',
                                        ],
                                    ],
                                },
                            },
                        ],
                    },
                    {
                        id: 'flowchart',
                        label: 'Flowchart',
                        icon: 'diagram',
                        blocks: [
                            {
                                type: 'heading',
                                headingText: 'Power of Holy Name',
                            },
                            {
                                type: 'diagram',
                                diagram: {
                                    mermaidCode: `flowchart LR
        S1_1{"Nāma may destroy sin, but how\\ncan nāmābhāsa atone thousands of sins\\nthat a thousand 12-year vows cannot?"}
        S1_2["Chanting (suniṣkṛtam) is the best\\natonement — it destroys the ROOT of\\nsin. A 12-year vow destroys only\\nits effects."]
        S1_3["Beyond that, the Lord's own mind\\nnow dwells on the chanter:\\n'This person belongs to Me,\\nI will always protect him.'"]
        S1_4["Vishnudutas' logic: if the Lord sent us\\nfor Ajāmila's dying, unconscious cry of\\n'Nārāyaṇa' — how much more will He\\nremember one who chants with real service?"]
        S1_5["From Ajāmila's very FIRST call of\\n'Nārāyaṇa,' all his sin was already\\ndestroyed — every chant after that\\nonly built bhakti."]
        S1_6{"But he kept sinning afterward\\n(drinking, the prostitute) — didn't he\\nneed to chant again at death to\\ndestroy those NEW sins?"}
        S1_7["No — SB 6.2.16 states chanting destroys\\nUNLIMITED sin, past, present and future,\\nlike fire (also Hari-bhakti-vilāsa 11.339,\\nSB 6.16.44, SB 5.1.35)."]
        S1_8["No time span is ever given for\\nnāmābhāsa's effect — so the FIRST\\nchanting must destroy all sin AND\\nignorance, its root cause."]
        S1_9{"Then why didn't sin visibly leave\\nAjāmila at once? Why did his\\nattachment and sin continue\\nfor so long?"}
        S1_10["His remaining karma was a mere\\nimpression — like a toothless snake's\\nharmless bite. And the Lord Himself\\nlets sin linger a while, so paths like\\nkarma-kāṇḍa aren't completely uprooted."]

        S1_1 --> S1_2 --> S1_3 --> S1_4 --> S1_5 --> S1_6 --> S1_7 --> S1_8 --> S1_9 --> S1_10`,
                                },
                            },
                            {
                                type: 'heading',
                                headingText: 'An Insight into Offenses',
                            },
                            {
                                type: 'diagram',
                                diagram: {
                                    mermaidCode: `flowchart LR
        S2_1["To interpret scripture's praise of\\nthe name as mere EXAGGERATION is\\nitself an offense (Padma Purāṇa:\\ntathārtha-vādo hari-nāmni kalpanam)."]
        S2_2["Such interpreters fall to hell, or\\nstay in hell forever — Padma Purāṇa,\\nKātyāyana-saṁhitā and Jaimini-saṁhitā\\nall agree."]
        S2_3["Parīkṣit Mahārāja: 'I think atonement\\nis useless, like an elephant bathing' —\\nsince the sinful tendency always returns."]
        S2_4["He condemns ATONEMENT, not bhakti\\nitself — even devotees may show sinful\\ntendencies without his criticizing bhakti."]
        S2_5["Ajāmila, a sinner, attained Vaikuṇṭha\\nby nāmābhāsa alone; smārtas, though\\nscripture-wise and chanting, remain in\\nfrightful saṁsāra — the offense of\\ninterpreting the name."]
        S2_6["Still, one shouldn't expect everyone\\nto be liberated immediately just from\\nseeing this power in the name."]
        S2_7["In most cases the name shows its\\nfruit only after some time — like a\\ntree bearing fruit late, not the\\nmoment it is planted."]
        S2_8["In some cases the name shows no\\nvisible effect at all — so faith in\\nscriptures like karma-kāṇḍa is not\\ncompletely destroyed."]
        S2_9["Eventually, the name itself carries\\nthe offenseless chanter to the\\nLord's abode."]

        S2_1 --> S2_2 --> S2_3 --> S2_4 --> S2_5 --> S2_6 --> S2_7 --> S2_8 --> S2_9`,
                                },
                            },
                            {
                                type: 'heading',
                                headingText: 'Glory of Bhakti and Chanting over Offenses',
                            },
                            {
                                type: 'diagram',
                                diagram: {
                                    mermaidCode: `flowchart LR
        S3_1["Why do even karmīs, jñānīs, and\\nbhaktas still experience karma's\\neffects, if the name destroys all sin?"]
        S3_2{"If chanting destroys ALL sin, they\\nshouldn't go to hell when they chant.\\nIf it doesn't, even bhaktas should\\ngo to hell too."}
        S3_3["The name mirrors a merchant: protection\\nmatches the degree of surrender; offense\\ndispleases him; mercy grows back as the\\noffense diminishes."]
        S3_4["Karmīs/jñānīs hold bhakti as merely\\nSECONDARY to karma/jñāna — worse than\\nthe literal 8th offense of equating the\\nname with karma-kāṇḍa."]
        S3_5["Out of compassion for even this small\\nshelter, Bhakti-devī still delivers the\\nresults of karma/jñāna without\\nobstruction."]
        S3_6["But as a limb of ATONEMENT, she destroys\\nsin only for those actually performing\\natonement — others go to hell for their\\nsins; Vaiṣṇavas alone need no atonement."]
        S3_7["If such a person ALSO offends the name\\nor a devotee, she gives NO results even\\nfor karma/dharma (Padma Purāṇa: offenses\\ndestroy pious acts, return one to\\nmaterial life)."]
        S3_8["Once free of offense and devoted to\\nchanting, results come in proportion to\\noffense destroyed; full destruction via\\ndevotee association makes chanting's\\nfruit certain."]
        S3_9{"But Ajāmila was previously doing\\nkarma, only secondary bhakti, per\\nthe Yamadūtas' own account —\\nshouldn't his sin have remained?"}
        S3_10["True — his sin had destroyed his\\nbrahminical status AND his pious\\nkarma; his secondary bhakti was\\ndestroyed too. PURE bhakti appeared\\nonly when he called out 'Nārāyaṇa.'"]
        S3_11{"But scripture permits bhakti as a\\nmere limb of karma/jñāna — how can\\nthat be offensive?"}
        S3_12["Such permissive statements exist only\\nfor people of crooked mind who lack\\nfaith in bhakti — not authoritative for\\nothers; like violence in sacrifice, the\\noffense is never erased even when\\nkarma's results come."]
        S3_13["Accepting a Vaiṣṇava guru and holding\\nbhakti as primary (though mixed) makes\\none a Vaiṣṇava: offenses destroyed by\\ndegree of bhakti, prema attained by\\ndegree of Bhakti-devī's mercy\\n(SB 11.14.26, 11.2.42, 1.2.17)."]
        S3_14["Offenders ascend through the FOURTEEN\\nSTAGES of bhakti (faith and further\\nsteps prescribed); once all offenses\\nare destroyed, they attain the Lord."]
        S3_15["For those WITHOUT offense, only TWO\\nstages remain: chanting, then Vaikuṇṭha\\n— as with Ajāmila (Mahābhārata\\n13.135.131, SB 4.25.29)."]
        S3_16["Some offense-free devotees desiring a\\nSPECIAL prema are delayed — Jaḍa\\nBharata took three lifetimes."]
        S3_17["Even devotees WITH offense who continue\\nto sin never fall to hell — Yamarāja\\nhimself commands his men, 'I am master\\nof others, but not the Vaiṣṇavas'\\n(SB 6.3.27, 11.29.20)."]
        S3_18["Since even the sprout of bhakti is\\nimperishable, any further birth is only\\nso bhakti can bear fruit — never leftover\\nkarma; such a devotee's happiness and\\ndistress come directly from the Lord\\n(SB 1.5.19, SB 10.87.40)."]
        S3_19["As bhakti increases, the root of sin is\\ndestroyed and the Lord is attained —\\nthough to deepen that bhakti, one, two,\\nor three more lifetimes may follow.\\nApparent hardship is the Lord acting like\\na doctor prescribing bitter medicine\\n(SB 10.88.8)."]
        S3_20["Among the ten offenses: interpreting the\\nname as exaggeration, and equating it\\nwith karma-kāṇḍa rites, are OBSTACLES TO\\nBEING A VAIṢṆAVA. Offending devotees, and\\nsinning on the strength of chanting, are\\ntwo other especially strong offenses."]
        S3_21["For those two strong offenses: no remedy\\nexcept continuing to chant WHILE suffering\\nappropriately. Every other offense is\\ndestroyed simply by continual chanting —\\nWITHOUT such suffering."]

        S3_1 --> S3_2 --> S3_3 --> S3_4 --> S3_5 --> S3_6 --> S3_7 --> S3_8 --> S3_9 --> S3_10 --> S3_11 --> S3_12 --> S3_13 --> S3_14 --> S3_15 --> S3_16 --> S3_17 --> S3_18 --> S3_19 --> S3_20 --> S3_21`,
                                },
                            },
                            {
                                type: 'heading',
                                headingText: 'Need of Guru',
                            },
                            {
                                type: 'diagram',
                                diagram: {
                                    mermaidCode: `flowchart LR
        S4_1["Proposition: offenders without karma/jñāna,\\npracticing bhakti through hearing and chanting\\nbut never formally initiated, are still\\n'Vaiṣṇavas' by Pāṇini's grammatical definition —\\nso they too shouldn't fall to hell."]
        S4_2["Not correct — one cannot easily cross\\nmaterial existence without a guru\\n(SB 11.20.17: the human body is a boat,\\nthe guru is its captain)."]
        S4_3{"But Ajāmila attained the Lord\\nwithout ever surrendering\\nto a guru."}
        S4_4["Those who merely chase sense objects\\ncan't know the Lord, bhakti, or guru\\neven in dreams — but like Ajāmila, they\\nCAN be delivered without a guru through\\noffenseless nāmābhāsa (Padyāvalī: the\\nname doesn't wait for initiation)."]
        S4_5["But one who thinks, 'Why bother with a\\nguru? I'll attain the Lord just by\\nchanting,' will NOT attain Him — this is\\nitself the offense of disregarding guru\\n(the third offense)."]
        S4_6["If, in this life or another, he later\\nsurrenders to a guru's feet and destroys\\nthat offense, he will then attain\\nthe Lord."]

        S4_1 --> S4_2 --> S4_3 --> S4_4 --> S4_5 --> S4_6`,
                                },
                            },
                            {
                                type: 'heading',
                                headingText: 'Further Mercy of Lord',
                            },
                            {
                                type: 'diagram',
                                diagram: {
                                    mermaidCode: `flowchart LR
        S5_1["Some say worshippers of devatās should\\nbe ranked with karmīs (BG 9.23: they\\nworship the Lord too, but 'by the wrong\\nmethod'); others say Bhakti-devī ranks\\nthem even LOWER, lacking general surrender."]
        S5_2["For those who are simply offenders,\\nthere is NO deliverance at all — the\\nLord casts them into demonic wombs\\n(BG 16.19)."]
        S5_3["Yet some say even such offenses can be\\ndestroyed by mental ABSORPTION in the\\nLord — even through hatred or fear\\n(SB 7.1.30); repeated chanting, even with\\ninitial offense, eventually clears it\\n(Padma Purāṇa)."]
        S5_4["Others define 'absorption' as sustained,\\nrepeated meditation for it to work;\\nothers hold there is no fixed rule at all\\nfor when Kṛṣṇa personally appears\\nin the world."]
        S5_5["Some attained the Lord with no absorption\\nat all, simply by being slain by Him\\ndirectly — Narakāsura, Bāṇāsura, and the\\nKaurava armies, purely 'by the influence\\nof seeing' Him."]

        S5_1 --> S5_2 --> S5_3 --> S5_4 --> S5_5`,
                                },
                            },
                        ],
                    },
                ],
            },
        },
    ],
};

export default content;
